import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { coreValuePageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

function useRevealOnScroll() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.1 }
    );
    el.querySelectorAll(".reveal").forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function CoreValue() {
  const revealRef = useRevealOnScroll();

  return (
    <>
      <SEO
        title="核心價值｜時光逆行駭客｜老化程式除錯四大協議"
        description="時光逆行駭客的四大核心價值：讀取狀態、除錯分析、重寫程式、持續監控。以程式除錯思維，為老化狀態進行全面診斷與優化。"
        keywords="核心價值,老化除錯,逆齡,時光逆行駭客,醫美,抗衰老"
        canonicalPath="/core-value"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "核心價值｜時光逆行駭客",
          "description": "時光逆行駭客的四大核心價值：讀取狀態、除錯分析、重寫程式、持續監控。",
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg" ref={revealRef}>
      {/* ===== HERO ===== */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 circuit-bg opacity-20" />
        {/* Decorative floating chips */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[
            { top: "15%", left: "8%", text: "{ aging: true }" },
            { top: "25%", right: "10%", text: "debug(mode)" },
            { top: "60%", left: "5%", text: "const reverse =" },
            { top: "70%", right: "8%", text: "optimize(life)" },
          ].map((chip, i) => (
            <div
              key={i}
              className="absolute font-mono text-neon-cyan/10 text-base whitespace-nowrap hidden lg:block"
              style={{ top: chip.top, left: chip.left, right: chip.right }}
            >
              {chip.text}
            </div>
          ))}
        </div>
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center max-w-4xl mx-auto">
            <span className="section-label reveal opacity-0 translate-y-6 transition-all duration-700">
              {coreValuePageData.heroTagline}
            </span>
            <h1 className="mt-5 text-[2rem] leading-[1.3] md:text-[2.75rem] md:leading-[1.25] font-bold text-ghost reveal opacity-0 translate-y-6 transition-all duration-700 delay-100">
              時光逆行駭客的
              <br />
              <span className="text-gradient-gold">核心價值</span>
            </h1>
            <p className="mt-6 text-ghost-muted text-[1.1875rem] leading-[1.75] reveal opacity-0 translate-y-6 transition-all duration-700 delay-200">
              {coreValuePageData.heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* ===== FOUR CORE VALUE CARDS ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {coreValuePageData.cards.map((card, idx) => (
              <div
                key={idx}
                className="card-cyber p-6 md:p-10 reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  {/* Icon */}
                  <div className="shrink-0">
                    <div className="w-16 h-16 rounded-2xl bg-cyber-gold/10 border border-cyber-gold/20 flex items-center justify-center">
                      <i className={`${card.icon} text-cyber-gold text-2xl`} />
                    </div>
                  </div>
                  <div className="flex-1">
                    {/* Number + Subtitle */}
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-neon-cyan text-base">
                        PROTOCOL_{card.num}
                      </span>
                    </div>
                    <h3 className="text-[1.25rem] leading-[1.5] md:text-[1.625rem] md:leading-[1.45] font-semibold text-ghost mb-1">
                      {card.title}
                    </h3>
                    <p className="font-mono text-cyber-gold/60 text-base mb-4">
                      {card.subtitle}
                    </p>
                    <p className="text-ghost-muted text-[1.0625rem] leading-[1.7]">
                      {card.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== VISUAL DIVIDER: Data Flow ===== */}
      <section className="py-12 md:py-16 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0">
              {["讀懂狀態", "找出原因", "設計方案", "持續優化"].map((step, idx) => (
                <div key={idx} className="flex items-center gap-3 md:gap-0">
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 rounded-full border border-cyber-gold/30 bg-cyber-gold/5 flex items-center justify-center">
                      <span className="font-mono text-cyber-gold text-[1.25rem] font-bold">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <span className="mt-2 text-ghost text-[1.0625rem] font-medium">{step}</span>
                  </div>
                  {idx < 3 && (
                    <div className="hidden md:flex items-center px-6">
                      <div className="w-12 h-px bg-cyber-gold/20" />
                      <i className="ri-arrow-right-line text-cyber-gold/40 text-base ml-1" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="card-cyber p-10 md:p-16 text-center reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{coreValuePageData.bottomCta.label}</span>
            <h2 className="mt-5 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {coreValuePageData.bottomCta.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-line text-[1.0625rem]"
              >
                <i className="ri-line-fill" />
                {coreValuePageData.bottomCta.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold text-[1.0625rem]">
                <i className="ri-calendar-check-line" />
                {coreValuePageData.bottomCta.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}
